type CompareFields<T> = string[] | ((item: T, localItem: T) => boolean);

interface SyncOptions<T> {
  compareFields?: CompareFields<T>;
}

/**
 * Smart sync logic for optimistic list updates.
 * Only returns true if server data has meaningful changes that should update local state.
 *
 * @param serverData - The latest data from the server
 * @param localData - The current local/optimistic data
 * @param options - Configuration for field comparison
 * @returns true if local state should be updated with server data
 */
export function shouldSyncList<T extends Record<string, any> & { id: number | string }>(
  serverData: T[] | null | undefined,
  localData: T[] | null | undefined,
  options: SyncOptions<T> = {}
): boolean {
  const { compareFields } = options;
  const nextItems = serverData || [];
  const currentLocal = localData || [];

  // Skip when both sides are empty; otherwise a fresh `[]` per render causes an infinite loop.
  if (currentLocal.length === 0) {
    return nextItems.length > 0;
  }

  if (nextItems.length !== currentLocal.length) {
    return true;
  }

  const localIdList = currentLocal.map((item) => item.id);
  const localIds = new Set(localIdList);
  const hasNew = nextItems.some((item) => !localIds.has(item.id));
  if (hasNew) {
    return true;
  }

  const orderChanged = nextItems.some((item, idx) => item.id !== localIdList[idx]);
  if (orderChanged) {
    return true;
  }

  const localById = new Map(currentLocal.map((item) => [item.id, item]));
  const hasUpdates = nextItems.some((item) => {
    const local = localById.get(item.id);
    if (!local) return false;

    if (typeof compareFields === 'function') {
      return compareFields(item, local);
    }

    if (Array.isArray(compareFields)) {
      return compareFields.some((field) => {
        const getNestedValue = (obj: any, path: string) => {
          return path.split('.').reduce((acc, part) => acc?.[part], obj);
        };

        const serverValue = getNestedValue(item, field);
        const localValue = getNestedValue(local, field);

        if (
          typeof serverValue === 'object' &&
          serverValue !== null &&
          typeof localValue === 'object' &&
          localValue !== null
        ) {
          return JSON.stringify(serverValue) !== JSON.stringify(localValue);
        }

        return serverValue !== localValue;
      });
    }

    return JSON.stringify(item) !== JSON.stringify(local);
  });

  return hasUpdates;
}
